import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  addItemExtra,
  fetchWasserijProducts,
  removeItemExtra,
  type WasserijItemChildEntry,
  type WasserijProduct,
} from '../../lib/wasserij-items';
import { formatCurrency } from '../../lib/format';
import { isExtraIncludedInPackage } from '../../lib/pricing/vloerkleed';

interface Props {
  itemId: string;
  parentProductId: string;
  extras: WasserijItemChildEntry[];
  packageCode?: string | null;
  onChanged: () => void;
  onPreviewChange?: (extras: Array<{ id: string; name: string; price: number }>) => void;
}

export function ExtrasEditor({
  itemId,
  parentProductId,
  extras,
  packageCode = null,
  onChanged,
  onPreviewChange,
}: Props) {
  const [products, setProducts] = useState<WasserijProduct[] | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [busyCodes, setBusyCodes] = useState<Set<string>>(new Set());
  const [pendingAdd, setPendingAdd] = useState<Set<string>>(new Set());
  const [pendingRemove, setPendingRemove] = useState<Set<string>>(new Set());
  const [localChildIds, setLocalChildIds] = useState<Map<string, string>>(new Map());
  const [actionError, setActionError] = useState<string | null>(null);
  const inflightRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingProducts(true);
    setProductsError(null);
    fetchWasserijProducts('extra', parentProductId, { itemId })
      .then((list) => {
        if (!cancelled) setProducts(list);
      })
      .catch((err: Error) => {
        if (!cancelled) setProductsError(err.message || "Kon extra's niet laden");
      })
      .finally(() => {
        if (!cancelled) setLoadingProducts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [parentProductId, itemId]);

  const extrasByCode = useMemo(() => {
    const map = new Map<string, WasserijItemChildEntry>();
    for (const e of extras) map.set(e.code, e);
    return map;
  }, [extras]);

  useEffect(() => {
    const known = new Set(extras.map((e) => e.code));
    setPendingAdd((prev) => {
      const next = new Set([...prev].filter((c) => !known.has(c)));
      return next.size === prev.size ? prev : next;
    });
    setPendingRemove((prev) => {
      const next = new Set([...prev].filter((c) => known.has(c)));
      return next.size === prev.size ? prev : next;
    });
  }, [extras]);

  const isOn = (code: string) => {
    if (pendingRemove.has(code)) return false;
    if (pendingAdd.has(code)) return true;
    return extrasByCode.has(code);
  };

  useEffect(() => {
    if (!onPreviewChange || !products) return;
    onPreviewChange(
      products
        .filter(
          (p) => isOn(p.code) && !isExtraIncludedInPackage(p.code, packageCode, p.name)
        )
        .map((p) => {
          const child = extrasByCode.get(p.code);
          const price = child?.priceInclBtw ?? p.priceInclBtw ?? 0;
          return { id: child?.id ?? p.id, name: p.name, price };
        })
        .filter((e) => e.price > 0)
    );
  }, [onPreviewChange, products, extrasByCode, pendingAdd, pendingRemove, packageCode]);

  const handleToggle = async (product: WasserijProduct) => {
    if (isExtraIncludedInPackage(product.code, packageCode, product.name)) return;
    if (inflightRef.current || busyCodes.size > 0) return;
    inflightRef.current = true;

    const currentlyOn = isOn(product.code);
    const existing = extrasByCode.get(product.code);

    setActionError(null);
    setBusyCodes((prev) => new Set(prev).add(product.code));
    if (currentlyOn) {
      setPendingRemove((prev) => new Set(prev).add(product.code));
      setPendingAdd((prev) => {
        const next = new Set(prev);
        next.delete(product.code);
        return next;
      });
    } else {
      setPendingAdd((prev) => new Set(prev).add(product.code));
      setPendingRemove((prev) => {
        const next = new Set(prev);
        next.delete(product.code);
        return next;
      });
    }

    try {
      if (currentlyOn) {
        const childId = existing?.id ?? localChildIds.get(product.code);
        if (!childId) throw new Error('Extra is nog niet opgeslagen; probeer het zo opnieuw.');
        await removeItemExtra(childId);
        setLocalChildIds((prev) => {
          const next = new Map(prev);
          next.delete(product.code);
          return next;
        });
      } else {
        const { childItemId } = await addItemExtra(itemId, product.id);
        if (childItemId) {
          setLocalChildIds((prev) => new Map(prev).set(product.code, childItemId));
        }
      }
      onChanged();
    } catch (err) {
      if (currentlyOn) {
        setPendingRemove((prev) => {
          const next = new Set(prev);
          next.delete(product.code);
          return next;
        });
      } else {
        setPendingAdd((prev) => {
          const next = new Set(prev);
          next.delete(product.code);
          return next;
        });
      }
      setActionError(
        err instanceof Error
          ? err.message
          : currentlyOn
            ? 'Extra verwijderen mislukt'
            : 'Extra toevoegen mislukt'
      );
    } finally {
      inflightRef.current = false;
      setBusyCodes((prev) => {
        const next = new Set(prev);
        next.delete(product.code);
        return next;
      });
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="mb-2">
        <h3 className="text-xs uppercase tracking-wide text-slate-500 font-medium">Extra's</h3>
        <p className="text-[11px] text-slate-400 mt-0.5">Vinken slaat meteen op.</p>
      </div>

      {loadingProducts && <p className="text-xs text-slate-500">Extra's laden…</p>}
      {productsError && <p className="text-xs text-red-600">{productsError}</p>}
      {products && products.length === 0 && (
        <p className="text-sm text-slate-400">Geen extra's beschikbaar voor dit hoofdproduct.</p>
      )}
      {products && products.length > 0 && (
        <div className="space-y-1.5">
          {products.map((p) => {
            const included = isExtraIncludedInPackage(p.code, packageCode, p.name);
            const added = isOn(p.code);
            const child = extrasByCode.get(p.code);
            const busy = busyCodes.has(p.code);
            const anyBusy = busyCodes.size > 0;
            const catalogPrice = p.priceInclBtw ?? null;
            const shownPrice = included
              ? null
              : child && added
                ? child.priceInclBtw
                : catalogPrice;
            return (
              <label
                key={p.id}
                className={clsx(
                  'flex items-center gap-2.5 px-3 py-2 rounded-md border',
                  included
                    ? 'border-emerald-200 bg-emerald-50'
                    : added
                      ? 'border-indigo-300 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300',
                  included || anyBusy ? 'cursor-default' : 'cursor-pointer'
                )}
              >
                <input
                  type="checkbox"
                  disabled={included || anyBusy}
                  checked={added || included}
                  onChange={() => void handleToggle(p)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-800 flex-1">{p.name}</span>
                {included ? (
                  <span className="text-xs text-emerald-700">inbegrepen</span>
                ) : shownPrice != null ? (
                  <span className="text-xs font-mono text-slate-600">{formatCurrency(shownPrice)}</span>
                ) : null}
                {busy && <span className="text-xs text-slate-400">…</span>}
              </label>
            );
          })}
        </div>
      )}
      {actionError && <p className="mt-2 text-xs text-red-600">{actionError}</p>}
    </div>
  );
}
